import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch'

import { shouldRefitToViewportOnResize } from './shouldRefitToViewportOnResize'

type ImageNatural = { w: number; h: number }

type RestoredTransform = {
  scale: number
  positionX: number
  positionY: number
}

/**
 * The transform wrapper's untouched initial state — no fit, restore, or user
 * gesture ever leaves the viewer here, so it marks a write that never landed.
 */
export const isVirginTransform = (state: {
  scale: number
  positionX: number
  positionY: number
}) => state.scale === 1 && state.positionX === 0 && state.positionY === 0

export const useFitToViewport = (
  transformRef: React.RefObject<ReactZoomPanPinchRef | null>,
  setMinScale: (scale: number) => void,
  imageDimensions: ImageNatural,
  isMultipage: boolean,
  allImagesLoaded: boolean,
  paddingPx: number = 0,
  autoFitEnabled: boolean = true,
  restoredTransform: RestoredTransform | null = null,
  resetKey?: string,
  preserveViewportOnImageResize = false
) => {
  const [fitComplete, setFitComplete] = useState(true)
  const pendingRestoreRef = useRef(restoredTransform)
  const hasAppliedInitialTransformRef = useRef(false)
  const lastFitImageSizeRef = useRef<ImageNatural | null>(null)
  const restoredTransformRef = useRef(restoredTransform)
  restoredTransformRef.current = restoredTransform
  const lastResetKeyRef = useRef(resetKey)
  const minScaleRef = useRef(0)
  const lastWrapperSizeRef = useRef({ w: 0, h: 0 })

  // The viewer is not remounted when its tab shows a different document, so
  // the applied-fit memory below would otherwise carry one document's pan and
  // zoom onto the next whenever their page sizes match. A resetKey change
  // re-arms the initial fit (or the caller's freshly restored transform).
  useEffect(() => {
    if (lastResetKeyRef.current === resetKey) return
    lastResetKeyRef.current = resetKey
    hasAppliedInitialTransformRef.current = false
    lastFitImageSizeRef.current = null
    minScaleRef.current = 0
    lastWrapperSizeRef.current = { w: 0, h: 0 }
    pendingRestoreRef.current = restoredTransformRef.current
  }, [resetKey])

  const calculateFitScale = useCallback(() => {
    if (!imageDimensions || !transformRef.current || !allImagesLoaded)
      return null

    const wrapper = transformRef.current.instance.wrapperComponent
    const contentComponent = transformRef.current.instance.contentComponent

    if (!wrapper || !contentComponent) return null

    const wrapRect = wrapper.getBoundingClientRect()

    if (wrapRect.width === 0 || wrapRect.height === 0) return null

    // Offset dimensions are the layout size at scale 1: unlike a client rect
    // divided by transformState.scale, they stay valid when a transform
    // animation is mid-flight and the rect and state disagree.
    const contentWidthAtScale1 = contentComponent.offsetWidth

    if (isMultipage) {
      const actualContentWidth = contentWidthAtScale1
      const actualContentHeight = contentComponent.offsetHeight

      const scaleX = (wrapRect.width - paddingPx * 2) / actualContentWidth
      const scaleY = (wrapRect.height - paddingPx * 2) / actualContentHeight
      const targetScale = Math.min(scaleX, scaleY)

      return {
        targetScale,
        initialScaleX: 1,
        wrapRect,
        actualContentWidth,
        actualContentHeight,
      }
    } else {
      const initialScaleX = contentWidthAtScale1 / imageDimensions.w

      const safeInitialScaleX =
        initialScaleX > 0 && initialScaleX < 1000 && !isNaN(initialScaleX)
          ? initialScaleX
          : 1

      const scaleX = (wrapRect.width - paddingPx * 2) / imageDimensions.w
      const scaleY = (wrapRect.height - paddingPx * 2) / imageDimensions.h
      const targetScaleInImageSpace = Math.min(scaleX, scaleY)

      return {
        targetScale: targetScaleInImageSpace / safeInitialScaleX,
        initialScaleX: safeInitialScaleX,
        wrapRect,
      }
    }
  }, [imageDimensions, transformRef, isMultipage, allImagesLoaded, paddingPx])

  const fitImageToViewport = useCallback(
    (animationTime = 0) => {
      if (!transformRef.current) return

      const result = calculateFitScale()
      if (!result) return

      const { targetScale, initialScaleX, wrapRect } = result

      setMinScale(targetScale)
      minScaleRef.current = targetScale
      lastWrapperSizeRef.current = { w: wrapRect.width, h: wrapRect.height }

      const isMultipageCalculation =
        isMultipage &&
        'actualContentWidth' in result &&
        result.actualContentWidth !== undefined &&
        result.actualContentHeight !== undefined &&
        imageDimensions

      const imgCenterX = imageDimensions.w / 2
      const imgCenterY = imageDimensions.h / 2

      const centerInDisplayX = isMultipageCalculation
        ? imgCenterX
        : imgCenterX * initialScaleX
      const centerInDisplayY = isMultipageCalculation
        ? imgCenterY
        : imgCenterY * initialScaleX

      const positionX = wrapRect.width / 2 - centerInDisplayX * targetScale
      const positionY = wrapRect.height / 2 - centerInDisplayY * targetScale

      transformRef.current.setTransform(
        positionX,
        positionY,
        targetScale,
        animationTime
      )
    },
    [imageDimensions, calculateFitScale, transformRef, setMinScale, isMultipage]
  )

  const resetToFit = useCallback(() => {
    fitImageToViewport(200)
  }, [fitImageToViewport])

  useEffect(() => {
    if (!imageDimensions || !allImagesLoaded) return

    const handleViewportChange = () => {
      const result = calculateFitScale()
      if (!result) return

      const currentScale =
        transformRef.current?.instance.transformState.scale ?? 0
      const previousSize = lastWrapperSizeRef.current
      const shouldRefit = shouldRefitToViewportOnResize({
        newMinScale: result.targetScale,
        previousMinScale: minScaleRef.current,
        currentScale,
        wrapperWidth: result.wrapRect.width,
        wrapperHeight: result.wrapRect.height,
        previousWrapperWidth: previousSize.w,
        previousWrapperHeight: previousSize.h,
      })

      setMinScale(result.targetScale)
      minScaleRef.current = result.targetScale
      lastWrapperSizeRef.current = {
        w: result.wrapRect.width,
        h: result.wrapRect.height,
      }

      if (autoFitEnabled && shouldRefit) {
        fitImageToViewport(0)
      }
    }

    window.addEventListener('resize', handleViewportChange)

    let observer: ResizeObserver | undefined
    const wrapper = transformRef.current?.instance.wrapperComponent
    if (wrapper instanceof Element && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(handleViewportChange)
      observer.observe(wrapper)
    }

    return () => {
      window.removeEventListener('resize', handleViewportChange)
      observer?.disconnect()
    }
  }, [
    allImagesLoaded,
    autoFitEnabled,
    calculateFitScale,
    fitImageToViewport,
    imageDimensions,
    setMinScale,
    transformRef,
  ])

  // Fit on load, or restore a Doc Nav tab's saved transform when remounting.
  useEffect(() => {
    if (imageDimensions && transformRef.current && allImagesLoaded) {
      if (!autoFitEnabled) {
        setFitComplete(true)
        return
      }

      // A transform written here can be lost when a dev remount
      // re-initializes the transform wrapper AFTER this effect ran — the
      // reset applies on a later frame, so even a healthy-looking check
      // right now proves nothing. The wrapper comes back in its virgin
      // state (scale 1 at the origin), which a real fit, restore, or user
      // gesture never leaves behind, so every path below leaves behind
      // delayed checks that re-apply if the viewer turns virgin.
      const isVirgin = () => {
        const state = transformRef.current?.instance.transformState
        return !!state && isVirginTransform(state)
      }
      const scheduleVirginChecks = (apply: () => void) =>
        [100, 400].map((delay) =>
          setTimeout(() => {
            if (isVirgin()) apply()
          }, delay)
        )

      // A virgin saved viewport can only be the wrapper's own initialization
      // that leaked into the store — restoring it would just reproduce the
      // unfitted state, so fall through to fit.
      const pendingRestore =
        pendingRestoreRef.current &&
        !isVirginTransform(pendingRestoreRef.current)
          ? pendingRestoreRef.current
          : null
      pendingRestoreRef.current = null
      if (pendingRestore) {
        const fit = calculateFitScale()
        if (fit) {
          setMinScale(fit.targetScale)
          minScaleRef.current = fit.targetScale
          lastWrapperSizeRef.current = {
            w: fit.wrapRect.width,
            h: fit.wrapRect.height,
          }
        }
        hasAppliedInitialTransformRef.current = true
        lastFitImageSizeRef.current = imageDimensions
        const applyRestore = () => {
          transformRef.current?.setTransform(
            pendingRestore.positionX,
            pendingRestore.positionY,
            pendingRestore.scale,
            0
          )
        }
        applyRestore()
        setFitComplete(true)
        const timers = scheduleVirginChecks(applyRestore)
        return () => timers.forEach(clearTimeout)
      }

      const lastSize = lastFitImageSizeRef.current
      const sizeUnchanged =
        lastSize !== null &&
        lastSize.w === imageDimensions.w &&
        lastSize.h === imageDimensions.h
      if (
        preserveViewportOnImageResize &&
        !isMultipage &&
        hasAppliedInitialTransformRef.current &&
        !sizeUnchanged &&
        !isVirgin() &&
        minScaleRef.current > 0
      ) {
        const fit = calculateFitScale()
        if (fit) {
          const current = transformRef.current.instance.transformState
          // Keep the zoom relative to fit and the displayed origin unchanged
          // when a higher-resolution image replaces the same page's preview.
          const scale = current.scale * (fit.targetScale / minScaleRef.current)
          const { positionX, positionY } = current
          setMinScale(fit.targetScale)
          minScaleRef.current = fit.targetScale
          lastWrapperSizeRef.current = {
            w: fit.wrapRect.width,
            h: fit.wrapRect.height,
          }
          lastFitImageSizeRef.current = imageDimensions
          const applyPreservedViewport = () => {
            transformRef.current?.setTransform(positionX, positionY, scale, 0)
          }
          applyPreservedViewport()
          setFitComplete(true)
          const timers = scheduleVirginChecks(applyPreservedViewport)
          return () => timers.forEach(clearTimeout)
        }
      }
      if (
        hasAppliedInitialTransformRef.current &&
        sizeUnchanged &&
        !isVirgin()
      ) {
        setFitComplete(true)
        const timers = scheduleVirginChecks(() => fitImageToViewport(0))
        return () => timers.forEach(clearTimeout)
      }

      hasAppliedInitialTransformRef.current = true
      lastFitImageSizeRef.current = imageDimensions
      fitImageToViewport(0)
      const timers = [
        setTimeout(() => setFitComplete(true), 100),
        ...scheduleVirginChecks(() => fitImageToViewport(0)),
      ]
      return () => timers.forEach(clearTimeout)
    }

    setFitComplete(false)
  }, [
    allImagesLoaded,
    autoFitEnabled,
    calculateFitScale,
    fitImageToViewport,
    imageDimensions,
    isMultipage,
    preserveViewportOnImageResize,
    resetKey,
    setMinScale,
    transformRef,
  ])

  return {
    imgNatural: imageDimensions,
    resetToFit,
    onFitComplete: fitComplete,
  }
}
